/**
 * Organization Selection Page
 * 
 * Shown when:
 * - User has access to multiple organizations
 * - User was removed from their active org but has other orgs
 * - User needs to choose which org to work in
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Building2, Users, Crown, AlertTriangle, Plus, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Alert, AlertDescription } from '../components/ui/alert';
import { toast } from 'sonner';
import api from '../services/api';

const OrgSelectPage = () => {
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState(null);
  
  // Get context from session storage (set by auth callback)
  const accessMessage = sessionStorage.getItem('access_message');
  const removedFromOrg = sessionStorage.getItem('removed_from_org');
  const availableOrgsFromSession = sessionStorage.getItem('available_orgs');

  useEffect(() => {
    const fetchOrganizations = async () => {
      try {
        // Try to get orgs from session first (from auth callback)
        if (availableOrgsFromSession) {
          const parsed = JSON.parse(availableOrgsFromSession);
          setOrganizations(parsed.map(org => ({
            organization_id: org.org_id,
            name: org.org_name,
            role: org.role,
            is_current: false
          })));
          setLoading(false);
          return;
        }
        
        // Otherwise fetch from API
        const response = await api.get('/api/auth/my-organizations');
        setOrganizations(response.data.organizations || []);
      } catch (error) {
        console.error('Failed to fetch organizations:', error);
        // If 403, user might not have any orgs - redirect to create
        if (error.response?.status === 403) {
          navigate('/onboarding/create-org');
          return;
        }
        toast.error('Failed to load organizations');
      } finally {
        setLoading(false);
      }
    };

    fetchOrganizations();
    
    // Clean up session storage
    return () => {
      sessionStorage.removeItem('access_message');
      sessionStorage.removeItem('removed_from_org');
      sessionStorage.removeItem('available_orgs');
    };
  }, [availableOrgsFromSession, navigate]);

  const handleSelectOrg = async (orgId) => {
    setSwitching(orgId);
    try {
      await api.post('/api/auth/switch-organization', { organization_id: orgId });
      toast.success('Switched organization');
      navigate('/dashboard', { replace: true });
    } catch (error) {
      console.error('Failed to switch organization:', error);
      toast.error('Failed to switch organization');
      setSwitching(null);
    }
  };

  const handleCreateOrg = () => {
    navigate('/onboarding/create-org');
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'owner':
        return <Crown className="w-4 h-4 text-amber-500" />;
      case 'product_manager':
        return <Users className="w-4 h-4 text-blue-500" />;
      default:
        return <Users className="w-4 h-4 text-gray-500" />;
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'owner':
        return 'Owner';
      case 'product_manager':
        return 'Product Manager';
      case 'collaborator':
        return 'Collaborator';
      default:
        return 'Member';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading your organizations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-blue-50 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Select Organization
          </h1>
          <p className="text-gray-600">
            Choose which organization you want to work in
          </p>
        </div>

        {/* Alert for removed users */}
        {removedFromOrg && (
          <Alert className="mb-6 border-amber-200 bg-amber-50">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-amber-800">
              {accessMessage || `You no longer have access to "${removedFromOrg}". Contact their admin to request re-access.`}
            </AlertDescription>
          </Alert>
        )}

        {/* Organization List */}
        <div className="space-y-3 mb-6">
          {organizations.map((org) => (
            <Card 
              key={org.organization_id}
              className={`cursor-pointer transition-all hover:shadow-md hover:border-blue-300 ${
                org.is_current ? 'border-blue-500 bg-blue-50/50' : ''
              }`}
              onClick={() => handleSelectOrg(org.organization_id)}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{org.name}</h3>
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        {getRoleIcon(org.role)}
                        <span>{getRoleLabel(org.role)}</span>
                        {org.is_current && (
                          <span className="ml-2 text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full">
                            Current
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button 
                    variant={org.is_current ? "outline" : "default"}
                    size="sm"
                    disabled={switching === org.organization_id}
                  >
                    {switching === org.organization_id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : org.is_current ? (
                      'Continue'
                    ) : (
                      'Select'
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Create New Organization Option */}
        <Card 
          className="cursor-pointer transition-all hover:shadow-md hover:border-green-300 border-dashed"
          onClick={handleCreateOrg}
        >
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-100 to-green-200 flex items-center justify-center">
                <Plus className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Create New Organization</h3>
                <p className="text-sm text-gray-500">Start fresh with a new workspace</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Help text */}
        {organizations.length === 0 && (
          <div className="text-center mt-8">
            <p className="text-gray-500 mb-4">
              You don't have access to any organizations yet.
            </p>
            <Button onClick={handleCreateOrg}>
              <Plus className="w-4 h-4 mr-2" />
              Create Your First Organization
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default OrgSelectPage;
